'use client'

import { AnimatedDivider } from '@ethena/shared-ui/src/components/AnimatedDivider'
import { SplineViewer } from '@ethena/shared-ui/src/components/SplineViewer'
import Image from 'next/image'

import { RxChevronDown } from 'react-icons/rx'

import { Hero } from '@/components/Hero'
import { ScrollContainer } from '@/components/ScrollContainer'
import { HeadlineStatsSection } from '@/components/Sections/HeadlineStatsSection'
import { useElementInView } from '@/hooks/useElementInView'
import { cn } from '@/utils'

export const HeroSection = ({
  isGerman,
  setCurrentSection,
}: {
  isGerman: boolean
  setCurrentSection: (section: number) => void
}) => {
  const isInView = useElementInView('hero-section')

  return (
    <section
      className={cn(
        'bg-background-primary w-full relative lg:layout-background-image lg:hero-layout lg:my-5 lg:mx-5 lg:h-[95dvh] lg:hlg:h-[97dvh] lg:rounded-4xl lg:border-2 lg:border-cblue-600/20 transition-all duration-500 ease-in-out',
        isInView && 'lg:border-2 lg:border-cblue-600',
      )}
    >
      <div className="absolute inset-x-0 top-0 h-[70vh] hlg:h-[750px] xl:hlg:h-[100vh] xl:h-[750px] lg:hidden">
        <Image
          src="/hero-ascii.avif"
          alt="Hero ASCII"
          width={1920}
          height={840}
          unoptimized
          priority
          className="w-full h-full object-cover object-top sm:object-bottom"
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(9,9,11,0)_20.84%,#09090B_91.52%)]" />
      </div>

      <div className="relative h-full flex flex-col xl:pt-12 xl:hsmmd:pt-0">
        <div className="flex-1 flex items-center min-h-0 pt-6 md:pt-20 md:hbase:pt-12">
          <div className="landing-section w-full relative h-full">
            <div className="landing-layout mx-auto w-full h-full flex items-center">
              <Hero isGerman={isGerman} />
            </div>

            <div className="absolute inset-y-0 right-0 sm:right-[4%] w-full sm:w-1/2 xl:right-[1%]">
              <div className="hidden sm:block sm:absolute top-1/2 -translate-y-1/2 w-full h-screen hbase:max-h-[45rem] max-h-[35rem] xl:max-h-[40rem] xl:hbase:max-h-[50rem]">
                <SplineViewer scene="/shared/spline/landing-v2.splinecode" className="pointer-events-auto" />
              </div>
            </div>
          </div>
        </div>

        {!isGerman && (
          <div className="w-full shrink-0">
            <div className="landing-section !py-0">
              <div className="landing-layout w-full mx-auto">
                <AnimatedDivider />
                <ScrollContainer>
                  <div className="pt-[24px] pb-6 hsmmd:pb-[40px] xl:pb-[24px]">
                    <HeadlineStatsSection />
                  </div>
                </ScrollContainer>
              </div>
            </div>
          </div>
        )}
      </div>
      {!isGerman && (
        <button
          onClick={() => {
            const nextSection = document.getElementById('section-1')

            if (nextSection) {
              nextSection.scrollIntoView({ behavior: 'smooth' })
              setCurrentSection(1)
            }
          }}
          aria-label="Scroll to next section"
          className="hidden xl:hsmmd:flex xl:hidden absolute bottom-0 left-1/2 -translate-x-1/2 w-fit justify-center p-2 group z-10"
        >
          <div className="relative w-10 h-10">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[115%] h-[115%] rounded-full bg-text-muted/10 animate-[pulse_2s_ease-in-out_infinite] blur-[2px]" />
            <RxChevronDown className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-text-muted/90 text-4xl group-hover:text-text-muted/70" />
          </div>
        </button>
      )}
    </section>
  )
}
